import { useEffect, useState } from "react";
import { API, rupiah, fmtDate, errMsg, ORDER_STATUS, imgSrc, PAYMENT_METHODS } from "@/lib/api";
import { toast } from "sonner";
import { X } from "lucide-react";

export default function OrdersAdmin() {
  const [orders, setOrders] = useState(null);
  const [selected, setSelected] = useState(null);
  const [ongkir, setOngkir] = useState("");
  const [status, setStatus] = useState("");
  const [courier, setCourier] = useState("");
  const [diskon, setDiskon] = useState("");
  const [alamat, setAlamat] = useState("");
  const [catatan, setCatatan] = useState("");
  const [payMethod, setPayMethod] = useState("cash");
  const [dueVal, setDueVal] = useState(1);
  const [dueUnit, setDueUnit] = useState("minggu");
  const [filter, setFilter] = useState("");

  const load = () => API.get("/orders").then((r) => setOrders(r.data)).catch(() => setOrders([]));
  useEffect(() => { load(); }, []);

  const openDetail = (o) => {
    setSelected(o);
    setOngkir(o.shipping_cost || "");
    setStatus(o.status);
    setCourier(o.courier || "");
    setDiskon(o.discount || "");
    setAlamat(o.shipping_address || "");
    setCatatan(o.notes || "");
    setPayMethod(o.payment_method || "cash");
    setDueVal(o.due_days || 1);
    setDueUnit("hari");
  };

  const hapus = async (o) => {
    if (!window.confirm(`Hapus pesanan ${o.order_number}? Invoice terkait ikut terhapus.`)) return;
    try {
      await API.delete(`/orders/${o.id}`);
      toast.success("Pesanan dihapus");
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const save = async () => {
    try {
      await API.put(`/orders/${selected.id}`, {
        shipping_cost: ongkir === "" ? undefined : Number(ongkir),
        discount: diskon === "" ? undefined : Number(diskon),
        status: status !== selected.status ? status : undefined,
        courier,
        shipping_address: alamat,
        notes: catatan,
        payment_method: payMethod,
        due_days: payMethod === "hutang" ? Number(dueVal) * (dueUnit === "minggu" ? 7 : dueUnit === "bulan" ? 30 : 1) : undefined,
      });
      toast.success("Pesanan diperbarui");
      setSelected(null);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const filtered = (orders || []).filter((o) => !filter || o.status === filter);
  const inputCls = "w-full bg-white border border-slate-300 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A227]";

  return (
    <div data-testid="admin-orders">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <h1 className="font-display text-2xl font-bold text-[#0B1F3A]">Pesanan Masuk</h1>
          <p className="text-sm text-slate-500">Atur ongkir, konfirmasi pembayaran, dan status pengiriman</p>
        </div>
        <select value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="orders-filter" className={inputCls + " w-auto"}>
          <option value="">Semua Status</option>
          {Object.entries(ORDER_STATUS).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm" data-testid="orders-table">
          <thead>
            <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-widest text-slate-400">
              <th className="px-5 py-3">No. Pesanan</th>
              <th className="px-5 py-3">Pelanggan</th>
              <th className="px-5 py-3">Total</th>
              <th className="px-5 py-3">Status</th>
              <th className="px-5 py-3">Tanggal</th>
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filtered.map((o) => {
              const st = ORDER_STATUS[o.status] || { label: o.status, color: "bg-slate-100" };
              return (
                <tr key={o.id} data-testid={`order-row-${o.id}`} className="hover:bg-slate-50">
                  <td className="px-5 py-3 font-mono-accent text-xs font-bold">{o.order_number}</td>
                  <td className="px-5 py-3">{o.customer_name}<span className="block text-xs text-slate-400">{o.customer_phone}</span></td>
                  <td className="px-5 py-3 font-bold">{rupiah(o.total)}</td>
                  <td className="px-5 py-3"><span className={`text-[10px] font-bold px-2 py-1 rounded-full ${st.color}`}>{st.label}</span></td>
                  <td className="px-5 py-3 text-xs text-slate-500">{fmtDate(o.created_at)}</td>
                  <td className="px-5 py-3 whitespace-nowrap">
                    <button onClick={() => openDetail(o)} data-testid={`order-manage-${o.id}`} className="px-4 py-1.5 bg-[#0B1F3A] text-white text-xs font-bold rounded-md hover:bg-[#132D52] transition-colors">
                      Kelola
                    </button>
                    <button onClick={() => hapus(o)} data-testid={`order-delete-${o.id}`} className="ml-2 px-3 py-1.5 border border-red-300 text-red-600 text-xs font-bold rounded-md hover:bg-red-50 transition-colors">
                      Hapus
                    </button>
                  </td>
                </tr>
              );
            })}
            {orders && filtered.length === 0 && (
              <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-400">Belum ada pesanan.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setSelected(null)} />
          <div className="relative bg-white rounded-md border border-slate-200 w-full max-w-lg max-h-[90vh] overflow-y-auto p-6" data-testid="order-dialog">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-bold">{selected.order_number}</h2>
              <button onClick={() => setSelected(null)} data-testid="order-dialog-close"><X className="h-5 w-5 text-slate-400" /></button>
            </div>
            <div className="space-y-2 text-sm mb-5">
              {selected.items.map((i, idx) => (
                <div key={idx} className="flex justify-between">
                  <span className="text-slate-600">{i.name} ({i.variant_label}) × {i.qty}</span>
                  <span className="font-semibold">{rupiah(i.subtotal)}</span>
                </div>
              ))}
              <div className="border-t border-slate-100 pt-2 flex justify-between text-xs text-slate-500">
                <span>Alamat: {selected.shipping_address}</span>
              </div>
              {selected.payment_proof && (
                <a href={imgSrc(selected.payment_proof)} target="_blank" rel="noreferrer" data-testid="view-proof" className="inline-block text-xs text-blue-600 hover:underline font-bold">
                  Lihat Bukti Pembayaran
                </a>
              )}
            </div>
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2 text-xs">
                <span className={`px-2.5 py-1 rounded-full font-bold ${selected.delivery_method === "ambil_sendiri" ? "bg-violet-100 text-violet-800" : "bg-slate-100 text-slate-700"}`} data-testid="order-delivery-badge">
                  {selected.delivery_method === "ambil_sendiri" ? "Ambil Sendiri di Toko" : "Pakai Jasa Pengiriman"}
                </span>
                <span className={`px-2.5 py-1 rounded-full font-bold ${(PAYMENT_METHODS[selected.payment_method] || PAYMENT_METHODS.transfer).color}`} data-testid="order-payment-badge">
                  {selected.payment_method === "hutang" ? `Hutang — jt. tempo ${selected.due_date || "-"}` : (PAYMENT_METHODS[selected.payment_method] || PAYMENT_METHODS.transfer).label}
                </span>
                {(selected.voucher_discount || 0) > 0 && (
                  <span className="px-2.5 py-1 rounded-full font-bold bg-purple-100 text-purple-800">Voucher {selected.voucher_code}: -{rupiah(selected.voucher_discount)}</span>
                )}
                {selected.invoice_number && (
                  <span className="px-2.5 py-1 rounded-full font-bold bg-slate-100 text-slate-700">Invoice: {selected.invoice_number}</span>
                )}
                {selected.sj_number && (
                  <span className="px-2.5 py-1 rounded-full font-bold bg-slate-100 text-slate-700">Surat Jalan: {selected.sj_number}</span>
                )}
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-slate-500 block mb-1.5">Metode Pembayaran</label>
                <select value={payMethod} onChange={(e) => setPayMethod(e.target.value)} data-testid="input-payment-method" className={inputCls}>
                  <option value="cash">Cash / Tunai</option>
                  <option value="transfer">Transfer Bank</option>
                  <option value="qris">QRIS</option>
                  <option value="hutang">Hutang (Tempo)</option>
                </select>
              </div>
              {payMethod === "hutang" && (
                <div>
                  <label className="text-xs font-bold uppercase tracking-widest text-slate-500 block mb-1.5">Jatuh Tempo (maks. 1 bulan dari tanggal pembelian)</label>
                  <div className="grid grid-cols-2 gap-2">
                    <input type="number" min="1" max={dueUnit === "hari" ? 31 : dueUnit === "minggu" ? 4 : 1} value={dueVal} onChange={(e) => setDueVal(e.target.value)} data-testid="input-due-val" className={inputCls} />
                    <select value={dueUnit} onChange={(e) => setDueUnit(e.target.value)} data-testid="input-due-unit" className={inputCls}>
                      <option value="hari">Hari</option>
                      <option value="minggu">Minggu</option>
                      <option value="bulan">Bulan</option>
                    </select>
                  </div>
                  <p className="text-xs text-slate-400 mt-1.5">Hanya pemilik yang dapat mengatur pembayaran tempo — maksimal 31 hari / 4 minggu / 1 bulan dari tanggal pembelian.</p>
                </div>
              )}
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-slate-500 block mb-1.5">Ongkos Kirim (Rp)</label>
                <input type="number" min="0" value={ongkir} onChange={(e) => setOngkir(e.target.value)} data-testid="input-ongkir" className={inputCls} placeholder="0" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-slate-500 block mb-1.5">Diskon Pemilik (Rp)</label>
                <input type="number" min="0" value={diskon} onChange={(e) => setDiskon(e.target.value)} data-testid="input-diskon" className={inputCls} placeholder="0" />
                <p className="text-xs text-slate-400 mt-1.5">Diskon khusus dari pemilik (misal pelanggan setia, pembelian grosir, hasil negosiasi) — memotong total pesanan &amp; invoice.</p>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-slate-500 block mb-1.5">Alamat Pengiriman</label>
                <textarea rows={2} value={alamat} onChange={(e) => setAlamat(e.target.value)} data-testid="input-alamat" className={inputCls} />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-slate-500 block mb-1.5">Catatan</label>
                <input value={catatan} onChange={(e) => setCatatan(e.target.value)} data-testid="input-catatan" className={inputCls} />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-slate-500 block mb-1.5">Kurir</label>
                <input value={courier} onChange={(e) => setCourier(e.target.value)} data-testid="input-courier" className={inputCls} placeholder="JNE / Cargo / dll" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-slate-500 block mb-1.5">Status</label>
                <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="input-status" className={inputCls}>
                  {Object.entries(ORDER_STATUS).map(([k, v]) => (
                    <option key={k} value={k}>{v.label}</option>
                  ))}
                </select>
                <p className="text-xs text-slate-400 mt-1.5">Status "Selesai" otomatis mencatat pemasukan &amp; mengurangi stok.</p>
              </div>
              <button onClick={save} data-testid="save-order" className="w-full px-6 py-3 bg-[#0B1F3A] bg-navy-hover text-white font-bold text-sm rounded-md transition-colors">
                Simpan Perubahan
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
